import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Form, Input, Modal, Segmented, Select, Space, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { createAssessment, listAssessments } from "./api";
import { LIGHT_COLORS, assessmentStatusLabels, formatDay, stateLabels } from "./format";

const NewAssessmentModal = ({ open, meta, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) form.setFieldsValue({ templateKey: meta?.templates?.[0]?.key || "", title: "" });
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const template = meta.templates.find(x => x.key === values.templateKey);
      const res = await createAssessment({
        templateKey: values.templateKey || undefined,
        ...(values.title || !template ? { title: values.title } : {})
      });
      history.push(`/user/safety/risk-assessments/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="safety-page"
      open={open}
      title={t("New risk assessment")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="templateKey"
          label={t("Template")}
          extra={t("The template fills in typical hazards and measures. Adapt them to the job.")}
        >
          <Select
            options={[
              ...(meta?.templates || []).map(x => ({
                value: x.key,
                label: `${x.title} (${x.hazards})`
              })),
              { value: "", label: t("Empty") }
            ]}
          />
        </Form.Item>
        <Form.Item
          name="title"
          label={t("Title")}
          dependencies={["templateKey"]}
          rules={[
            ({ getFieldValue }) => ({
              required: !getFieldValue("templateKey"),
              message: t("Please enter a title.")
            })
          ]}
        >
          <Input maxLength={200} placeholder={t("Empty: the template name")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/** Gefährdungsbeurteilungen per activity or site. */
const Assessments = ({ meta }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [status, setStatus] = useState("all");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const statuses = assessmentStatusLabels(t);
  const states = stateLabels(t);

  useEffect(() => {
    setLoading(true);
    listAssessments({ status: status === "all" ? undefined : status })
      .then(res => setData(res.data.data))
      .catch(generalErrorAlert)
      .finally(() => setLoading(false));
  }, [status]);

  return (
    <>
      <PageToolbar
        filters={
          <Segmented
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: t("All") },
              { value: "draft", label: statuses.draft },
              { value: "active", label: statuses.active },
              { value: "archived", label: statuses.archived }
            ]}
          />
        }
        actions={
          meta?.can.create && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
              {t("New risk assessment")}
            </Button>
          )
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={data}
        mobileAvatar={false}
        locale={{ emptyText: t("No risk assessments yet") }}
        pagination={data.length > 50 ? { pageSize: 50 } : false}
        onRow={record => ({
          onDoubleClick: () => history.push(`/user/safety/risk-assessments/${record._id}`)
        })}
        columns={[
          {
            title: t("Risk assessment"),
            key: "title",
            mobile: "title",
            render: (_, item) => (
              <div>
                <Link to={`/user/safety/risk-assessments/${item._id}`}>
                  <Typography.Text strong>{item.number}</Typography.Text>
                </Link>{" "}
                <span className="sf-wrap">{item.title}</span>
                <div className="sf-small sf-wrap">
                  {[item.site, item.project?.name].filter(Boolean).join(" · ")}
                </div>
              </div>
            )
          },
          {
            title: t("Status"),
            key: "status",
            mobile: "badge",
            render: (_, item) => (
              <Space size={[4, 4]} wrap>
                <Tag color={item.status === "active" ? "success" : "default"}>
                  {statuses[item.status]}
                </Tag>
                {item.reviewState && item.reviewState !== "ok" && (
                  <Tag color={LIGHT_COLORS[item.light]}>
                    {t("Review")}: {states[item.reviewState]}
                  </Tag>
                )}
              </Space>
            )
          },
          {
            title: t("High risks"),
            key: "high",
            align: "right",
            render: (_, item) => `${item.highRisks} / ${item.hazards}`
          },
          {
            title: t("Open measures"),
            dataIndex: "openMeasures",
            key: "open",
            align: "right"
          },
          { title: t("Review by"), key: "review", render: (_, item) => formatDay(item.reviewDate) }
        ]}
      />
      <NewAssessmentModal open={newOpen} meta={meta} onClose={() => setNewOpen(false)} />
    </>
  );
};

export default Assessments;
